import { useEffect, useRef, useState } from 'react'

import { t } from '../../../common/i18n.js'
import type { Language } from '../../../common/types/i18n.js'
import { LogOut, Menu, Search } from '../icon/solar-icons.js'
import { Link } from '../link/link.js'
import { PortalAvatar } from '../portal-avatar/portal-avatar.js'
import type { PortalHeroLink } from '../portal-hero/portal-hero.js'
import './portal-mobile-top-bar.css'

export interface PortalMobileTopBarProps {
    lang: Language
    userName: string
    avatarUrl?: string | null
    accountLinks: PortalHeroLink[]
    onMenuToggle: () => void
    onSearchOpen: () => void
    onLogout: () => void
}

export const PortalMobileTopBar = ({
    lang,
    userName,
    avatarUrl,
    accountLinks,
    onMenuToggle,
    onSearchOpen,
    onLogout,
}: PortalMobileTopBarProps) => {
    // The avatar opens a profile dropdown (account links + log out), mirroring
    // the desktop PortalHero user menu. Owns its own open/close + click-outside.
    const [isMenuOpen, setIsMenuOpen] = useState(false)
    const menuRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!isMenuOpen) return
        const onDocClick = (e: MouseEvent) => {
            if (!menuRef.current?.contains(e.target as Node)) setIsMenuOpen(false)
        }
        // Defer so the click that opened the menu isn't the one that closes it.
        const timer = setTimeout(() => document.addEventListener('click', onDocClick), 0)
        return () => {
            clearTimeout(timer)
            document.removeEventListener('click', onDocClick)
        }
    }, [isMenuOpen])

    return (
        <header className="portal-mobile-top-bar">
            <div className="portal-mobile-top-bar__left">
                <button
                    type="button"
                    className="portal-mobile-top-bar__hamburger"
                    onClick={onMenuToggle}
                    aria-label={t.common.openMenu(lang)}
                >
                    <Menu size={22} />
                </button>
                <span className="portal-mobile-top-bar__wordmark">Hub</span>
            </div>

            <div className="portal-mobile-top-bar__right">
                <button
                    type="button"
                    className="portal-mobile-top-bar__icon-btn"
                    onClick={onSearchOpen}
                    aria-label={t.common.search(lang)}
                >
                    <Search size={20} />
                </button>
                <div className="portal-mobile-top-bar__menu-wrapper" ref={menuRef}>
                    <button
                        type="button"
                        className="portal-mobile-top-bar__avatar-btn"
                        onClick={() => setIsMenuOpen((prev) => !prev)}
                        aria-label={t.common.profileMenu(lang)}
                        aria-haspopup="menu"
                        aria-expanded={isMenuOpen}
                    >
                        <PortalAvatar name={userName} avatarUrl={avatarUrl} px={32} />
                    </button>

                    {isMenuOpen && (
                        <div className="portal-mobile-top-bar__menu" role="menu">
                            {accountLinks.map((link) => (
                                <Link
                                    key={link.url}
                                    href={link.url || '#'}
                                    className="portal-mobile-top-bar__menu-item"
                                    role="menuitem"
                                    onClick={() => setIsMenuOpen(false)}
                                >
                                    {link.label}
                                </Link>
                            ))}
                            <hr className="portal-mobile-top-bar__menu-divider" />
                            <button
                                type="button"
                                className="portal-mobile-top-bar__menu-item portal-mobile-top-bar__menu-item--logout"
                                role="menuitem"
                                onClick={() => {
                                    setIsMenuOpen(false)
                                    onLogout()
                                }}
                            >
                                <LogOut size={16} />
                                {t.portal.nav.logOut(lang)}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </header>
    )
}
